import { Tooltip as ChakraTooltip, Portal } from '@chakra-ui/react';
import React from 'react';

export interface TooltipProps
	extends Omit<ChakraTooltip.RootProps, 'children'> {
	/**
	 * Child must be a single element so Tooltip.Trigger can clone it
	 * and attach refs/handlers when `asChild` is used.
	 */
	children: React.ReactElement;
	showArrow?: boolean;
	portalled?: boolean;
	portalRef?: React.RefObject<HTMLElement | null>;
	content: React.ReactNode;
	contentProps?: ChakraTooltip.ContentProps;
	disabled?: boolean;
	bg?: string;
	color?: string;
}

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
	function Tooltip(props, ref) {
		const {
			showArrow,
			children,
			disabled,
			portalled = true,
			content,
			contentProps,
			portalRef,
			bg,
			color,
			positioning,
			...rest
		} = props;

		const Trigger = ChakraTooltip.Trigger as unknown as React.ComponentType<{
			asChild?: boolean;
			children: React.ReactNode;
		}>;

		const Positioner =
			ChakraTooltip.Positioner as unknown as React.ComponentType<{
				children: React.ReactNode;
			}>;

		if (disabled) return children;


		const mergedPositioning = {
			strategy: portalled ? 'fixed' : 'absolute',
			flip: portalled ? undefined : false,
			...positioning,
		} as const;

		return (
			<ChakraTooltip.Root {...rest} positioning={mergedPositioning}>
				<Trigger asChild>{children}</Trigger>
				<Portal disabled={!portalled} container={portalRef}>
					<Positioner>
						<ChakraTooltip.Content
							ref={ref}
							{...contentProps}
							color={color}
							borderRadius="20px"
							style={{ '--tooltip-bg': bg }}
							zIndex={portalled ? 1500 : 10}
						>
							{showArrow && <ChakraTooltip.Arrow />}
							{content}
						</ChakraTooltip.Content>
					</Positioner>
				</Portal>
			</ChakraTooltip.Root>
		);
	},
);
